'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

declare global {
	interface Window {
		HelpGuidesChat?: HelpGuidesChat;
		HelpGuidesChatSettings?: {
			app_id: string;
			hide_default_launcher: boolean;
		};
	}
}

interface HelpGuidesChat {
	(...args: unknown[]): void;
	q?: unknown[];
}

const HELPGUIDES_APP_ID = '4025e8c9-71ea-4557-850d-662c78598919';
const HELPGUIDES_SCRIPT_SRC = 'https://trydocent.ai/chat/messenger.js';
const HELPGUIDES_SCRIPT_ID = 'help-guides-chat';

interface HelpGuidesChatBotProps {
	enabled?: boolean;
	/** Docent's `messenger.js` didn't load (an ad blocker, offline, Docent down): there is no chat to open. */
	onLoadError?: () => void;
}

export const HelpGuidesChatBot = ({ enabled = true, onLoadError }: HelpGuidesChatBotProps) => {
	const pathname = usePathname();
	// Read by the effect below, which should only re-run when `enabled` or the page changes.
	const onLoadErrorRef = useRef(onLoadError);

	useEffect(() => {
		onLoadErrorRef.current = onLoadError;
	}, [onLoadError]);

	useEffect(() => {
		if (!enabled) {
			window.HelpGuidesChat?.('hide');
			return;
		}

		if (!window.HelpGuidesChatSettings) {
			window.HelpGuidesChatSettings = {
				app_id: HELPGUIDES_APP_ID,
				hide_default_launcher: true
			};
		}

		const existingScript = document.getElementById(HELPGUIDES_SCRIPT_ID);

		if (!existingScript) {
			const chat: HelpGuidesChat = (...args: unknown[]) => {
				chat.q = chat.q || [];
				chat.q.push(args);
			};

			window.HelpGuidesChat = chat;

			const script = document.createElement('script');
			script.id = HELPGUIDES_SCRIPT_ID;
			script.async = true;
			script.src = HELPGUIDES_SCRIPT_SRC;
			script.addEventListener('error', () => {
				script.dataset.loadFailed = 'true';
				onLoadErrorRef.current?.();
			});
			document.head.appendChild(script);
			chat('boot', window.HelpGuidesChatSettings);
		} else if (existingScript.dataset.loadFailed) {
			onLoadErrorRef.current?.();
		} else {
			window.HelpGuidesChat?.('collapse');
		}
	}, [enabled, pathname]);

	return null;
};
